import { useCallback, useEffect, useRef, useState } from 'react';

import { useMintGscSelectionSessionMutation } from '../api/gscConnectionApi';
import { toGscMintFailureReason, type GscMintFailureReason } from './GscMintErrors';

export type GscMintedSessionConnection = {
  sessionId: string;
  sourceId: string;
};

export type GscMintedSession = {
  connection: GscMintedSessionConnection | null;
  isMinting: boolean;
  failure: GscMintFailureReason | null;
  /**
   * Remounts the picker: remint returns the same session id, so the id alone
   * is not a signal that anything was re-read.
   */
  mintCount: number;
  retry: () => void;
};

export default function useGscMintedSession(): GscMintedSession {
  const [mintSelectionSession, { isLoading: isMinting }] = useMintGscSelectionSessionMutation();
  const [connection, setConnection] = useState<GscMintedSessionConnection | null>(null);
  const [failure, setFailure] = useState<GscMintFailureReason | null>(null);
  const [mintCount, setMintCount] = useState(0);

  const mint = useCallback(async () => {
    setConnection(null);
    setFailure(null);
    setMintCount((count) => count + 1);
    try {
      const session = await mintSelectionSession().unwrap();
      setConnection({ sessionId: session.sessionId, sourceId: session.sourceId });
    } catch (err) {
      setFailure(toGscMintFailureReason(err));
    }
  }, [mintSelectionSession]);

  // Don't mint again when the mutation trigger identity changes: that would
  // hand the picker a different session than the one it seeded against.
  const hasMintedRef = useRef(false);
  useEffect(() => {
    if (hasMintedRef.current) {
      return;
    }
    hasMintedRef.current = true;
    void mint();
  }, [mint]);

  return {
    connection,
    isMinting,
    failure,
    mintCount,
    retry: () => {
      void mint();
    },
  };
}
